import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Shield, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Phase 4 — Slice C · Clubs Admin Page.
 *
 * Admin-only screen for managing PFAP + external clubs and their age groups.
 * PFAP singleton is visible but locked from rename/delete.
 */
const ClubsAdminPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [newClub, setNewClub] = useState({ name: '', country: '', age_groups: '', notes: '' });
  const [agInput, setAgInput] = useState({}); // { clubId: "U13" }

  const fetchClubs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clubs`);
      setClubs(r.data || []);
    } catch {
      toast.error('Failed to load clubs');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchClubs(); }, [fetchClubs]);

  const filtered = useMemo(() => {
    if (!search.trim()) return clubs;
    const q = search.toLowerCase();
    return clubs.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.country || '').toLowerCase().includes(q)
    );
  }, [clubs, search]);

  const handleCreate = async () => {
    const name = newClub.name.trim();
    if (!name) { toast.error('Name is required'); return; }
    const age_groups = newClub.age_groups.split(',').map(s => s.trim()).filter(Boolean);
    try {
      await axios.post(`${API}/clubs`, {
        name, country: newClub.country.trim() || null,
        age_groups, notes: newClub.notes.trim() || null,
      });
      toast.success(`Created club "${name}"`);
      setCreateOpen(false);
      setNewClub({ name: '', country: '', age_groups: '', notes: '' });
      fetchClubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create club');
    }
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    try {
      await axios.put(`${API}/clubs/${editTarget.id}`, {
        name: editTarget.name?.trim(),
        country: editTarget.country?.trim() || null,
        notes: editTarget.notes?.trim() || null,
      });
      toast.success('Club updated');
      setEditTarget(null);
      fetchClubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update club');
    }
  };

  const handleDelete = async (club) => {
    if (club.is_pfap) { toast.error('PFAP club cannot be deleted'); return; }
    if (!window.confirm(`Delete club "${club.name}"?`)) return;
    try {
      await axios.delete(`${API}/clubs/${club.id}`);
      toast.success(`Deleted "${club.name}"`);
      fetchClubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete club');
    }
  };

  const handleAddAg = async (clubId) => {
    const label = (agInput[clubId] || '').trim();
    if (!label) return;
    try {
      await axios.post(`${API}/clubs/${clubId}/age-groups`, { age_group: label });
      setAgInput(p => ({ ...p, [clubId]: '' }));
      fetchClubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add age group');
    }
  };

  const handleRemoveAg = async (clubId, ag) => {
    try {
      await axios.delete(`${API}/clubs/${clubId}/age-groups/${encodeURIComponent(ag)}`);
      fetchClubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove age group');
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500" data-testid="clubs-no-access">
          Only Admins can manage clubs.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="clubs-admin-page">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
              Club Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              PFAP + external clubs used across the Match System.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search clubs..."
                className="pl-8 w-56"
                data-testid="clubs-search-input"
              />
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-pfap-green hover:bg-pfap-green/90 text-white"
              data-testid="open-create-club"
            >
              <Plus className="w-4 h-4 mr-1" /> New Club
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <Card data-testid="clubs-empty-state">
            <CardContent className="py-12 text-center text-gray-500">
              No clubs match the search.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="clubs-grid">
            {filtered.map(club => (
              <Card key={club.id}
                    className="hover:shadow-md transition-shadow"
                    data-testid={`club-card-${club.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {club.is_pfap && (
                        <Shield className="w-4 h-4 text-amber-500" data-testid={`pfap-shield-${club.id}`} />
                      )}
                      <CardTitle className="text-lg" data-testid={`club-name-${club.id}`}>
                        {club.name}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-1">
                      {!club.is_pfap && (
                        <Button size="sm" variant="ghost"
                                onClick={() => setEditTarget({ ...club })}
                                data-testid={`edit-club-${club.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {!club.is_pfap && (
                        <Button size="sm" variant="ghost"
                                onClick={() => handleDelete(club)}
                                data-testid={`delete-club-${club.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <CardDescription>
                    {club.country || (club.is_pfap ? 'Portugal' : '—')}
                    {club.is_pfap && <span className="ml-2 text-xs text-amber-600">(protected)</span>}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs text-gray-500 uppercase tracking-wide">Age Groups</Label>
                    <div className="flex flex-wrap gap-1 mt-1.5 min-h-[28px]"
                         data-testid={`club-age-groups-${club.id}`}>
                      {(club.age_groups || []).length === 0 ? (
                        <span className="text-xs text-gray-400">No age groups yet</span>
                      ) : (
                        (club.age_groups || []).map(ag => (
                          <Badge key={ag} variant="outline"
                                 className="text-xs gap-1"
                                 data-testid={`club-ag-${club.id}-${ag}`}>
                            {ag}
                            <button
                              type="button"
                              onClick={() => handleRemoveAg(club.id, ag)}
                              className="ml-0.5 hover:text-red-500"
                              data-testid={`remove-ag-${club.id}-${ag}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))
                      )}
                    </div>
                    <div className="flex gap-1 mt-2">
                      <Input
                        value={agInput[club.id] || ''}
                        onChange={e => setAgInput(p => ({ ...p, [club.id]: e.target.value }))}
                        placeholder="Add age group e.g. U17"
                        onKeyDown={e => e.key === 'Enter' && handleAddAg(club.id)}
                        className="h-8 text-sm"
                        data-testid={`ag-input-${club.id}`}
                      />
                      <Button size="sm" onClick={() => handleAddAg(club.id)}
                              data-testid={`add-ag-${club.id}`}>
                        Add
                      </Button>
                    </div>
                  </div>
                  {club.notes && (
                    <div className="text-xs text-gray-500 italic border-t pt-2">
                      {club.notes}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent data-testid="create-club-dialog">
            <DialogHeader>
              <DialogTitle>New Club</DialogTitle>
              <DialogDescription>Add an external club to PFAP Hub.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="club-name">Name *</Label>
                <Input
                  id="club-name"
                  value={newClub.name}
                  onChange={e => setNewClub({ ...newClub, name: e.target.value })}
                  placeholder="e.g. SC Beira-Mar"
                  data-testid="create-club-name"
                />
              </div>
              <div>
                <Label htmlFor="club-country">Country</Label>
                <Input
                  id="club-country"
                  value={newClub.country}
                  onChange={e => setNewClub({ ...newClub, country: e.target.value })}
                  placeholder="Portugal"
                  data-testid="create-club-country"
                />
              </div>
              <div>
                <Label htmlFor="club-ags">Age Groups (comma-separated)</Label>
                <Input
                  id="club-ags"
                  value={newClub.age_groups}
                  onChange={e => setNewClub({ ...newClub, age_groups: e.target.value })}
                  placeholder="U13, U15, U17, U19"
                  data-testid="create-club-age-groups"
                />
              </div>
              <div>
                <Label htmlFor="club-notes">Notes</Label>
                <Textarea
                  id="club-notes"
                  value={newClub.notes}
                  onChange={e => setNewClub({ ...newClub, notes: e.target.value })}
                  rows={2}
                  data-testid="create-club-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} data-testid="submit-create-club">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit dialog */}
        <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
          <DialogContent data-testid="edit-club-dialog">
            <DialogHeader>
              <DialogTitle>Edit {editTarget?.name}</DialogTitle>
              <DialogDescription>Update club details. Name must be unique.</DialogDescription>
            </DialogHeader>
            {editTarget && (
              <div className="space-y-3 py-2">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editTarget.name || ''}
                    onChange={e => setEditTarget({ ...editTarget, name: e.target.value })}
                    data-testid="edit-club-name"
                  />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input
                    value={editTarget.country || ''}
                    onChange={e => setEditTarget({ ...editTarget, country: e.target.value })}
                    data-testid="edit-club-country"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    rows={2}
                    value={editTarget.notes || ''}
                    onChange={e => setEditTarget({ ...editTarget, notes: e.target.value })}
                    data-testid="edit-club-notes"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
              <Button onClick={handleUpdate} data-testid="submit-edit-club">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ClubsAdminPage;
